import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  fetchOfficialDetailBundle, partyBg, levelBadge, platformIcon, personTypeBadge, personTypeLabel, impactBadge,
  type Official, type OfficialSocialAccount, type OfficialActivity, type AccountabilityRecord,
  type CachedBriefing, type CachedVotingRecord, type CachedPublicRecord, type CachedScore,
  type CachedCampaignFinance, type CachedLegislativeRecord, type DossierStatus, type BlueskyThreadData, type BlueskyReply,
  type ArchiveSnapshotRecord, type ArchiveSnapshotSummary, type ArchivedOfficialReference,
} from './apiClient';
import type { AssetAuditRecord } from './types';
import { buildArchiveHistoryTimeline } from './officialDetailArchive';
import { formatOfficialStatusLabel } from './officialListLifecycle';
import { featureFlags } from './featureFlags';
import AdoptButton from './src/components/AdoptButton';
import ContributionForm from './src/components/ContributionForm';
import OfficialAvatar from './src/components/OfficialAvatar';
import CaseTimeline from './src/components/dossier/CaseTimeline';
import FreshnessBadge from './src/components/dossier/FreshnessBadge';
import LatestMovementPanel from './src/components/dossier/LatestMovementPanel';
import ReceiptCard from './src/components/share/ReceiptCard';
import { SpineRecommendations, SpineShareCard, scoreTier } from './src/components/SpineScore';
import { useCaseFileStyle } from './src/context/CaseFileStyleContext';
import { useAuth } from './src/context/AuthContext';
import { CaseFileScoreCard } from './src/components/casefile/CaseFileScoreModule';
import { RedFlagBadges, countActiveFlags } from './src/components/casefile/RedFlagBadges';
import { buildCaseTimeline } from './src/lib/dossierTimeline';
import { buildReceiptMoments } from './src/lib/share';

// ---------------------------------------------------------------------------
// Section splitter — turns markdown text into discrete visual sections
// ---------------------------------------------------------------------------

interface ContentSection {
  heading: string;
  content: string;
}

function splitSections(text: string): ContentSection[] {
  if (!text) return [];
  const sections: ContentSection[] = [];
  const parts = text.split(/^## /m);

  // Content before any heading
  const preamble = parts[0].trim();
  if (preamble) {
    sections.push({ heading: '', content: preamble });
  }

  for (let i = 1; i < parts.length; i++) {
    const newlineIdx = parts[i].indexOf('\n');
    if (newlineIdx === -1) {
      sections.push({ heading: parts[i].trim(), content: '' });
    } else {
      sections.push({
        heading: parts[i].slice(0, newlineIdx).trim(),
        content: parts[i].slice(newlineIdx + 1).trim(),
      });
    }
  }

  return sections;
}

function mapSnapshotAssets(assets: any[] = []): AssetAuditRecord[] {
  return assets.map((asset: any) => ({
    id: asset.id,
    type: asset.asset_type ?? asset.type,
    title: asset.title,
    uri: asset.uri,
    timestamp: asset.discovered_at ?? asset.timestamp,
    confidence: asset.confidence ?? 'MEDIUM',
  }));
}

// Section color/icon mapping based on heading keywords
function getSectionTheme(heading: string): { accent: string; bg: string; icon: string } {
  const h = heading.toLowerCase();
  if (h.includes('background') || h.includes('career') || h.includes('overview'))
    return { accent: 'border-l-blue-500', bg: 'bg-blue-500/5', icon: 'user' };
  if (h.includes('policy') || h.includes('position') || h.includes('legislative'))
    return { accent: 'border-l-purple-500', bg: 'bg-purple-500/5', icon: 'scale' };
  if (h.includes('controvers') || h.includes('accountability') || h.includes('concern') || h.includes('against'))
    return { accent: 'border-l-red-500', bg: 'bg-red-500/5', icon: 'alert' };
  if (h.includes('recent') || h.includes('development') || h.includes('news'))
    return { accent: 'border-l-amber-500', bg: 'bg-amber-500/5', icon: 'clock' };
  if (h.includes('perception') || h.includes('media') || h.includes('public'))
    return { accent: 'border-l-emerald-500', bg: 'bg-emerald-500/5', icon: 'eye' };
  if (h.includes('civil') || h.includes('rights') || h.includes('libert'))
    return { accent: 'border-l-violet-500', bg: 'bg-violet-500/5', icon: 'shield' };
  if (h.includes('health'))
    return { accent: 'border-l-rose-500', bg: 'bg-rose-500/5', icon: 'heart' };
  if (h.includes('environment') || h.includes('climate'))
    return { accent: 'border-l-green-500', bg: 'bg-green-500/5', icon: 'leaf' };
  if (h.includes('consumer') || h.includes('worker') || h.includes('labor'))
    return { accent: 'border-l-orange-500', bg: 'bg-orange-500/5', icon: 'briefcase' };
  if (h.includes('transparen') || h.includes('government'))
    return { accent: 'border-l-cyan-500', bg: 'bg-cyan-500/5', icon: 'search' };
  if (h.includes('voting') || h.includes('summary') || h.includes('analysis'))
    return { accent: 'border-l-indigo-500', bg: 'bg-indigo-500/5', icon: 'chart' };
  if (h.includes('legal') || h.includes('court') || h.includes('lawsuit'))
    return { accent: 'border-l-red-400', bg: 'bg-red-400/5', icon: 'gavel' };
  if (h.includes('financial') || h.includes('disclosure') || h.includes('campaign'))
    return { accent: 'border-l-yellow-500', bg: 'bg-yellow-500/5', icon: 'dollar' };
  if (h.includes('statement'))
    return { accent: 'border-l-sky-500', bg: 'bg-sky-500/5', icon: 'quote' };
  return { accent: 'border-l-slate-600', bg: 'bg-cream-200 dark:bg-navy-700/30', icon: 'doc' };
}

const SectionIcon: React.FC<{ type: string }> = ({ type }) => {
  const cls = "w-4 h-4";
  switch (type) {
    case 'user': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
    case 'scale': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>;
    case 'alert': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
    case 'clock': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    case 'eye': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
    case 'shield': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
    case 'heart': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>;
    case 'search': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
    default: return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
  }
};

// ---------------------------------------------------------------------------
// Inline markdown for paragraph content (bold, italic, links)
// ---------------------------------------------------------------------------

function InlineMarkdown({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(\[([^\]]+)\]\(([^)]+)\))|(`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let k = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1]) parts.push(<strong key={k++} className="font-semibold text-navy-900 dark:text-cream-50">{match[2]}</strong>);
    else if (match[3]) parts.push(<em key={k++} className="italic text-navy-700 dark:text-cream-200">{match[4]}</em>);
    else if (match[5]) parts.push(<a key={k++} href={match[7]} target="_blank" rel="noopener noreferrer" className="text-gold-600 dark:text-gold-400 hover:text-gold-500 underline underline-offset-2">{match[6]}</a>);
    else if (match[8]) parts.push(<code key={k++} className="text-[11px] bg-cream-200 dark:bg-navy-700 text-gold-500 px-1 py-0.5 rounded font-mono">{match[9]}</code>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

// Helper: detect table separator row (|---|---|)
function isTableSeparator(line: string): boolean {
  return /^\|[\s:-]+(\|[\s:.-]+)*\|?\s*$/.test(line.trim());
}

// Helper: detect table row (| cell | cell |)
function isTableRow(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') && t.includes('|', 1);
}

// Render markdown content within a section card (sub-headings, lists, paragraphs, tables)
function SectionContent({ content }: { content: string }) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') { i++; continue; }

    // Any heading (# through ####)
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const cls = level <= 2
        ? 'text-sm font-bold text-navy-800 dark:text-cream-100 mt-5 mb-2'
        : 'text-xs font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wide mt-4 mb-2';
      elements.push(<h4 key={key++} className={cls}><InlineMarkdown text={headingMatch[2]} /></h4>);
      i++; continue;
    }

    // Horizontal rule (---, ***, ___)
    if (/^[-*_]{3,}\s*$/.test(line.trim())) {
      elements.push(<hr key={key++} className="my-4 border-cream-300 dark:border-navy-600" />);
      i++; continue;
    }

    // Markdown table
    if (isTableRow(line)) {
      const headerRow: string[] | null = [];
      const dataRows: string[][] = [];
      let hasHeader = false;

      // Collect all contiguous table lines
      while (i < lines.length && (isTableRow(lines[i]) || isTableSeparator(lines[i]))) {
        const row = lines[i].trim();
        if (isTableSeparator(row)) {
          // The row before the separator is the header
          if (dataRows.length === 0 && headerRow.length === 0) {
            // Separator before any data — skip
          } else {
            hasHeader = true;
          }
          i++; continue;
        }
        const cells = row.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim());
        if (!hasHeader && headerRow.length === 0 && dataRows.length === 0) {
          headerRow.push(...cells);
          dataRows.push(cells);
        } else {
          dataRows.push(cells);
        }
        i++;
      }

      if (dataRows.length > 0) {
        const hRow = hasHeader ? dataRows[0] : null;
        const bRows = hasHeader ? dataRows.slice(1) : dataRows;
        elements.push(
          <div key={key++} className="overflow-x-auto my-3 rounded-lg border border-cream-300 dark:border-navy-600">
            <table className="w-full text-[12px] text-navy-700 dark:text-cream-200">
              {hRow && (
                <thead>
                  <tr className="border-b-2 border-cream-300 dark:border-navy-600 bg-cream-100/50 dark:bg-navy-800/40">
                    {hRow.map((cell, ci) => (
                      <th key={ci} className="px-3 py-2 text-left font-bold text-navy-800 dark:text-cream-100 text-[11px] uppercase tracking-wide">
                        <InlineMarkdown text={cell} />
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {bRows.map((row, ri) => (
                  <tr key={ri} className={`border-b border-cream-200 dark:border-navy-700 ${ri % 2 === 1 ? 'bg-cream-100/30 dark:bg-navy-800/20' : ''}`}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-3 py-2 leading-relaxed">
                        <InlineMarkdown text={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
      }
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(<li key={key++} className="text-[13px] text-navy-700 dark:text-cream-200 leading-relaxed"><InlineMarkdown text={lines[i].replace(/^\s*[-*+]\s+/, '')} /></li>);
        i++;
      }
      elements.push(<ul key={key++} className="space-y-2 my-3 ml-4">{items.map((item, idx) => <div key={idx} className="flex gap-2"><span className="text-navy-400 dark:text-navy-200 mt-1.5 shrink-0">&#8226;</span>{item}</div>)}</ul>);
      continue;
    }

    // Ordered list
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      let num = 1;
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(<li key={key++} className="text-[13px] text-navy-700 dark:text-cream-200 leading-relaxed flex gap-2"><span className="text-navy-400 dark:text-navy-300 font-bold shrink-0">{num}.</span><span><InlineMarkdown text={lines[i].replace(/^\s*\d+[.)]\s+/, '')} /></span></li>);
        i++; num++;
      }
      elements.push(<ol key={key++} className="space-y-2 my-3">{items}</ol>);
      continue;
    }

    // Paragraph — collect lines that aren't special formatting
    const pLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^#{1,4}\s/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) &&
      !/^\s*\d+[.)]\s+/.test(lines[i]) &&
      !isTableRow(lines[i]) &&
      !/^[-*_]{3,}\s*$/.test(lines[i].trim())
    ) {
      pLines.push(lines[i]);
      i++;
    }
    if (pLines.length > 0) {
      elements.push(<p key={key++} className="text-[13px] text-navy-700 dark:text-cream-200 leading-[1.7] my-2"><InlineMarkdown text={pLines.join(' ')} /></p>);
    }
  }

  return <div>{elements}</div>;
}

// ---------------------------------------------------------------------------
// Section Card — a single visual section within the briefing
// ---------------------------------------------------------------------------

function IntelSectionCard({ section }: { section: ContentSection; key?: React.Key }) {
  const [expanded, setExpanded] = useState(false);
  const { style } = useCaseFileStyle();
  const theme = getSectionTheme(section.heading);
  const isReport = style === 'field-report';

  if (!section.heading && !section.content) return null;

  return (
    <div className={isReport
      ? 'border border-navy-900 overflow-hidden bg-white transition-all'
      : `rounded-xl border border-cream-300 dark:border-navy-600 overflow-hidden ${theme.bg} transition-all`
    }>
      {section.heading && (
        <button
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className={isReport
            ? 'w-full flex items-center gap-3 px-5 py-3 text-left bg-cream-100 hover:bg-cream-200 transition mono'
            : `w-full flex items-center gap-3 px-5 py-4 text-left border-l-4 ${theme.accent} hover:bg-navy-900/[0.02] dark:hover:bg-white/[0.02] transition`
          }
        >
          <span className="text-navy-500 dark:text-navy-300"><SectionIcon type={theme.icon} /></span>
          <h3 className={isReport ? 'text-xs font-bold text-navy-900 uppercase tracking-wide flex-1' : 'text-sm font-bold text-navy-900 dark:text-cream-50 flex-1'}>{section.heading}</h3>
          <svg className={`w-4 h-4 text-navy-400 dark:text-navy-300 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
      {expanded && section.content && (
        <div className={`px-5 pb-5 ${section.heading ? 'pt-0 ml-[4px] border-l-4 border-transparent' : 'pt-4'}`}>
          <SectionContent content={section.content} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paginated Table
// ---------------------------------------------------------------------------

function PaginatedTable<T>({
  data, columns, pageSize = 10, renderRow, emptyMessage = 'No data available',
}: {
  data: T[];
  columns: { key: string; label: string; className?: string }[];
  pageSize?: number;
  renderRow: (item: T, index: number) => React.ReactNode;
  emptyMessage?: string;
}) {
  const { style } = useCaseFileStyle();
  const isReport = style === 'field-report';
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(data.length / pageSize);
  const pageData = data.slice(page * pageSize, (page + 1) * pageSize);

  if (data.length === 0) return <p className="text-xs text-navy-400 dark:text-navy-300 p-6">{emptyMessage}</p>;

  return (
    <div>
      <div className="overflow-x-auto md:overflow-visible">
        <table className="mobile-stack-table w-full text-left border-collapse">
          <thead>
            {/* Field-report matches the mockup's "Record Log" table exactly —
                a solid navy header bar — instead of a plain bottom border. */}
            <tr className={isReport ? 'bg-navy-900' : 'border-b border-cream-300 dark:border-navy-600'}>
              {columns.map((col) => (
                <th key={col.key} className={`px-4 py-3 text-xs md:text-[9px] font-black uppercase tracking-widest ${isReport ? 'text-cream-50 mono' : 'text-navy-400 dark:text-navy-300'} ${col.className || ''}`}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/40">
            {pageData.map((item, i) => renderRow(item, page * pageSize + i))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-cream-300 dark:border-navy-600 gap-3">
          <span className="text-xs sm:text-[10px] text-navy-400 dark:text-navy-300">{page * pageSize + 1}&#8211;{Math.min((page + 1) * pageSize, data.length)} of {data.length}</span>
          <div className="flex items-center gap-2">
            <PageBtn onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</PageBtn>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} onClick={() => setPage(i)} className={`min-w-[44px] min-h-[44px] text-xs font-bold rounded-lg transition ${i === page ? 'bg-navy-800 dark:bg-gold-500 text-white dark:text-navy-900' : 'text-navy-400 dark:text-navy-300 hover:text-navy-900 dark:hover:text-cream-50 hover:bg-cream-200 dark:hover:bg-navy-700'}`}>{i + 1}</button>
            )).slice(Math.max(0, page - 2), page + 3)}
            <PageBtn onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</PageBtn>
          </div>
        </div>
      )}
    </div>
  );
}

function PageBtn({ onClick, disabled, children }: { onClick: () => void; disabled: boolean; children: React.ReactNode }) {
  return (
    <button onClick={onClick} disabled={disabled} className="min-h-[44px] px-4 py-2 text-xs font-bold uppercase rounded-lg text-navy-500 dark:text-navy-300 hover:text-navy-900 dark:hover:text-cream-50 hover:bg-cream-200 dark:hover:bg-navy-700 disabled:opacity-25 disabled:hover:bg-transparent transition">{children}</button>
  );
}

// ---------------------------------------------------------------------------
// Tab Navigation
// ---------------------------------------------------------------------------

type TabId = 'intel' | 'timeline' | 'spine' | 'voting' | 'public' | 'legislation' | 'finance' | 'sources';

function TabNav({ activeTab, onTabChange, tabs }: { activeTab: TabId; onTabChange: (t: TabId) => void; tabs: { id: TabId; label: string; count?: number }[] }) {
  const { style } = useCaseFileStyle();

  if (style === 'evidence-board') {
    // Underlined text tabs matching the mockup — a dark solid tab for the
    // active one against a shared bottom rule, not a pill button group.
    return (
      <div className="flex flex-nowrap gap-0.5 border-b-2 border-navy-900 w-max min-w-full">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => onTabChange(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] shrink-0 text-xs md:text-[10px] font-black uppercase tracking-wider transition ${activeTab === t.id ? 'bg-navy-900 text-cream-50' : 'bg-cream-200 text-navy-500 hover:text-navy-800'}`}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className={`text-[10px] md:text-[8px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === t.id ? 'bg-gold-500 text-navy-900' : 'bg-cream-300 text-navy-500'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>
    );
  }

  if (style === 'field-report') {
    // Square form-field tabs — a bottom-ruled row of mono labels, matching
    // the 2b numbered-form aesthetic instead of a rounded pill group.
    return (
      <div className="flex flex-nowrap gap-0 border-2 border-navy-900 w-max min-w-full bg-white">
        {tabs.map((t, i) => (
          <button
            key={t.id}
            onClick={() => onTabChange(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] shrink-0 mono text-xs md:text-[10px] font-bold uppercase tracking-wider transition ${i > 0 ? 'border-l-2 border-navy-900' : ''} ${activeTab === t.id ? 'bg-navy-900 text-cream-50' : 'bg-white text-navy-500 hover:bg-cream-100'}`}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className={`text-[10px] md:text-[8px] px-1.5 py-0.5 font-bold ${activeTab === t.id ? 'bg-gold-500 text-navy-900' : 'bg-cream-200 text-navy-500'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1 bg-white/80 dark:bg-navy-800/60 border border-cream-300 dark:border-navy-600 rounded-lg p-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onTabChange(t.id)}
          className={`flex items-center gap-2 px-3 md:px-4 py-2.5 min-h-[44px] text-xs md:text-[10px] font-bold uppercase tracking-wider rounded-md transition ${activeTab === t.id ? 'bg-cream-200 dark:bg-navy-700 text-navy-900 dark:text-cream-50 shadow-sm' : 'text-navy-400 dark:text-navy-300 hover:text-navy-700 dark:hover:text-cream-200'}`}
        >
          {t.label}
          {t.count !== undefined && t.count > 0 && (
            <span className={`text-[10px] md:text-[8px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === t.id ? 'bg-navy-800 dark:bg-gold-500 text-white dark:text-navy-900' : 'bg-cream-200 dark:bg-navy-700 text-navy-400 dark:text-navy-300'}`}>{t.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Finding Card (timeline style for public records)
// ---------------------------------------------------------------------------

function FindingTimeline({ findings, pageSize = 10 }: { findings: CachedPublicRecord['findings']; pageSize?: number }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(findings.length / pageSize);
  const visible = findings.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div>
      <div className="relative pl-6 space-y-0">
        {/* Timeline line */}
        <div className="absolute left-[7px] top-3 bottom-3 w-px bg-cream-200 dark:bg-navy-700" />
        {visible.map((f, i) => {
          const impactColor = f.impact_type === 'negative' ? 'bg-red-500' : f.impact_type === 'positive' ? 'bg-emerald-500' : 'bg-slate-600';
          const impactBorder = f.impact_type === 'negative' ? 'border-red-900/30' : f.impact_type === 'positive' ? 'border-emerald-900/30' : 'border-cream-300 dark:border-navy-600';
          return (
            <div key={i} className="relative pb-6 last:pb-0">
              {/* Dot */}
              <div className={`absolute left-[-20px] top-5 w-[9px] h-[9px] rounded-full ${impactColor} ring-4 ring-cream-50 dark:ring-navy-900`} />
              <div className={`bg-white/80 dark:bg-navy-800/60 border ${impactBorder} rounded-xl p-5 ml-2 hover:bg-white dark:bg-navy-800 transition`}>
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h4 className="text-sm font-bold text-navy-900 dark:text-cream-50 leading-snug flex-1">{f.title}</h4>
                  <span className={`text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shrink-0 ${f.impact_type === 'negative' ? 'bg-red-500/10 text-red-400' : f.impact_type === 'positive' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-cream-200 dark:bg-navy-700 text-navy-500 dark:text-navy-300'}`}>
                    {f.impact_type}
                  </span>
                </div>
                <p className="text-xs text-navy-500 dark:text-navy-300 leading-relaxed mb-3">{f.description}</p>
                <div className="flex items-center gap-4 text-[10px] text-navy-400 dark:text-navy-300">
                  {f.date && <span className="mono">{f.date}</span>}
                  {f.source && <span>{f.source}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 mt-2 border-t border-cream-200 dark:border-navy-600/40 gap-3">
          <span className="text-xs sm:text-[10px] text-navy-400 dark:text-navy-300">{page * pageSize + 1}&#8211;{Math.min((page + 1) * pageSize, findings.length)} of {findings.length}</span>
          <div className="flex gap-2">
            <PageBtn onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</PageBtn>
            <PageBtn onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</PageBtn>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score Ring
// ---------------------------------------------------------------------------

function ScoreRing({ score, size = 120 }: { score: number | null; size?: number }) {
  const s = score ?? 0;
  const r = (size - 12) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (s / 100) * c;
  const color = s >= 70 ? '#22c55e' : s >= 40 ? '#eab308' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E0D6C2" strokeWidth={6} />
        {score !== null && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6} strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-1000" />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-navy-900 dark:text-cream-50">{score ?? '--'}</span>
        <span className="text-[8px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Score</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

/**
 * Human-readable labels for the file-completeness checklist. Raw cache_type
 * keys (dossier_editor, geo_impact, accountability_score) mean nothing to a
 * citizen reading a dossier — this should read like a simple case file, not
 * an internal debug view. Keys not in this map are skipped entirely rather
 * than shown with their raw snake_case name.
 */
const SECTION_LABELS: Record<string, string> = {
  briefing: 'Background brief',
  voting_record: 'Voting record',
  public_record: 'Public statements',
  social_intel: 'Social media',
  legislative_record: 'Legislative record',
  campaign_finance: 'Campaign finance',
  financial: 'Financial disclosures',
};

const OfficialDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { style: caseFileStyle } = useCaseFileStyle();
  const { isAuthenticated } = useAuth();
  const [official, setOfficial] = useState<Official | null>(null);
  const [socialAccounts, setSocialAccounts] = useState<OfficialSocialAccount[]>([]);
  const [activity, setActivity] = useState<OfficialActivity[]>([]);
  const [accountabilityRecords, setAccountabilityRecords] = useState<AccountabilityRecord[]>([]);
  const [archiveHistory, setArchiveHistory] = useState<ArchiveSnapshotSummary[]>([]);
  const [latestArchiveSnapshot, setLatestArchiveSnapshot] = useState<ArchiveSnapshotRecord | null>(null);
  const [replacedByOfficial, setReplacedByOfficial] = useState<ArchivedOfficialReference | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dossier, setDossier] = useState<DossierStatus | null>(null);
  const [briefingText, setBriefingText] = useState('');
  const [votingRecord, setVotingRecord] = useState<CachedVotingRecord | null>(null);
  const [publicRecord, setPublicRecord] = useState<CachedPublicRecord | null>(null);
  const [legislativeRecord, setLegislativeRecord] = useState<CachedLegislativeRecord | null>(null);
  const [campaignFinance, setCampaignFinance] = useState<CachedCampaignFinance | null>(null);
  const [scoringData, setScoringData] = useState<CachedScore | null>(null);
  const [allAssets, setAllAssets] = useState<AssetAuditRecord[]>([]);
  const [researchHistory, setResearchHistory] = useState<{ query: string; answer: string; created_at: number }[]>([]);
  const [blueskyThread, setBlueskyThread] = useState<BlueskyThreadData | null>(null);
  const [loadingIntel, setLoadingIntel] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('intel');
  const [showContributionForm, setShowContributionForm] = useState(false);
  const [activeReceiptAnchorId, setActiveReceiptAnchorId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setLoadingIntel(true);
    setError(null);
    setDossier(null);
    setBriefingText('');
    setVotingRecord(null);
    setPublicRecord(null);
    setLegislativeRecord(null);
    setCampaignFinance(null);
    setScoringData(null);
    setAllAssets([]);
    setResearchHistory([]);
    setBlueskyThread(null);

    fetchOfficialDetailBundle(id)
      .then((data) => {
        setOfficial(data.official);
        setSocialAccounts(data.socialAccounts);
        setActivity(data.recentActivity);
        setAccountabilityRecords(data.accountabilityRecords ?? []);
        setArchiveHistory(data.archiveHistory ?? []);
        setLatestArchiveSnapshot(data.latestArchiveSnapshot ?? null);
        setReplacedByOfficial(data.replacedByOfficial ?? null);
        setBlueskyThread(data.blueskyThread ?? { hasPost: false });

        const archivedSnapshot = data.official.lifecycle_status === 'archived' ? data.latestArchiveSnapshot?.snapshot : null;
        if (archivedSnapshot) {
          setDossier(archivedSnapshot.dossierStatus ?? null);
          setBriefingText(String((archivedSnapshot.intelligence?.briefing as CachedBriefing | undefined)?.text ?? ''));
          setVotingRecord((archivedSnapshot.intelligence?.voting_record as CachedVotingRecord | undefined) ?? null);
          setPublicRecord((archivedSnapshot.intelligence?.public_record as CachedPublicRecord | undefined) ?? null);
          setLegislativeRecord((archivedSnapshot.intelligence?.legislative_record as CachedLegislativeRecord | undefined) ?? null);
          {
            const cf = archivedSnapshot.intelligence?.campaign_finance as (CachedCampaignFinance & { notFound?: boolean }) | undefined;
            setCampaignFinance(cf && !cf.notFound ? cf : null);
          }
          setScoringData((archivedSnapshot.intelligence?.accountability_score as CachedScore | undefined) ?? null);
          setAllAssets(mapSnapshotAssets(archivedSnapshot.assets));
          setResearchHistory((archivedSnapshot.research as any[]) ?? []);
          return;
        }

        setDossier(data.dossierStatus ?? null);
        setBriefingText(data.intelligence.briefing.hit && data.intelligence.briefing.data ? data.intelligence.briefing.data.text : '');
        setVotingRecord(data.intelligence.voting_record.hit && data.intelligence.voting_record.data ? data.intelligence.voting_record.data : null);
        setPublicRecord(data.intelligence.public_record.hit && data.intelligence.public_record.data ? data.intelligence.public_record.data : null);
        setLegislativeRecord(data.intelligence.legislative_record.hit && data.intelligence.legislative_record.data ? data.intelligence.legislative_record.data : null);
        // FEC enrichment caches a `{ notFound: true }` sentinel when it can't
        // match an official to a candidate — that's a valid cache hit, but
        // not the CachedCampaignFinance shape the rest of this page assumes.
        {
          const cfData = data.intelligence.campaign_finance.data as (CachedCampaignFinance & { notFound?: boolean }) | undefined;
          setCampaignFinance(data.intelligence.campaign_finance.hit && cfData && !cfData.notFound ? cfData : null);
        }
        setScoringData(data.intelligence.accountability_score.hit && data.intelligence.accountability_score.data ? data.intelligence.accountability_score.data : null);
        setAllAssets((data.assets ?? []).map((a: any) => ({
          id: a.id,
          type: a.asset_type ?? a.type,
          title: a.title,
          uri: a.uri,
          timestamp: a.discovered_at ?? a.timestamp,
          confidence: a.confidence ?? 'MEDIUM',
        })));
        setResearchHistory((data.research as any[]) ?? []);
      })
      .catch((err) => setError(err.message || 'Failed to load'))
      .finally(() => {
        setLoading(false);
        setLoadingIntel(false);
      });
  }, [id]);

  useEffect(() => {
    const syncHash = () => {
      const nextHash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      setActiveReceiptAnchorId(nextHash || null);
    };

    syncHash();
    window.addEventListener('hashchange', syncHash);
    return () => window.removeEventListener('hashchange', syncHash);
  }, []);

  const briefingSections = useMemo(() => splitSections(briefingText), [briefingText]);
  const votingAnalysisSections = useMemo(() => splitSections(votingRecord?.analysis || ''), [votingRecord?.analysis]);
  const publicRecordSections = useMemo(() => splitSections(publicRecord?.text || ''), [publicRecord?.text]);

  // Dynamic social share meta tags — set document.title + og/twitter tags based
  // on the loaded official. Cleaned up on unmount so stale tags don't leak.
  useEffect(() => {
    if (!official) return;

    const DEFAULT_OG_IMAGE = 'https://adoptacoward.com/og-default.png';
    const title = `${official.name} — Adopt-A-Coward Dossier`;
    const description = official.accountability_summary
      ? official.accountability_summary.slice(0, 160)
      : `Track the record, rhetoric, and receipts for ${official.name}, ${official.office_name || 'public official'}.`;
    const ogImage = official.photo_url || DEFAULT_OG_IMAGE;

    const previousTitle = document.title;
    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const [, key, name] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
        if (key && name) el.setAttribute(key, name);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const tags: { selector: string; attr: string; value: string }[] = [
      { selector: 'meta[property="og:title"]', attr: 'content', value: title },
      { selector: 'meta[property="og:description"]', attr: 'content', value: description },
      { selector: 'meta[property="og:image"]', attr: 'content', value: ogImage },
      { selector: 'meta[property="og:type"]', attr: 'content', value: 'profile' },
      { selector: 'meta[name="twitter:card"]', attr: 'content', value: 'summary_large_image' },
      { selector: 'meta[name="twitter:title"]', attr: 'content', value: title },
      { selector: 'meta[name="twitter:description"]', attr: 'content', value: description },
      { selector: 'meta[name="twitter:image"]', attr: 'content', value: ogImage },
    ];

    for (const t of tags) setMeta(t.selector, t.attr, t.value);

    return () => {
      document.title = previousTitle;
      for (const t of tags) {
        const el = document.head.querySelector<HTMLMetaElement>(t.selector);
        if (el) el.remove();
      }
    };
  }, [official]);

  if (loading) {
    return (
      <div className="flex justify-center py-32">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 bg-red-600 rounded-full animate-ping" />
          <span className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest animate-pulse">Loading Dossier...</span>
        </div>
      </div>
    );
  }

  if (error || !official) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-red-500 font-black uppercase tracking-widest mb-4">{error || 'Not found'}</p>
        <Link to="/officials" className="text-navy-400 dark:text-navy-300 text-xs font-black uppercase tracking-widest hover:text-navy-900 dark:text-cream-50 transition">Return to Database</Link>
      </div>
    );
  }

  const initials = (official.first_name?.[0] || '') + (official.last_name?.[0] || official.name[0] || '');
  const spineScore = official.accountability_score ?? scoringData?.score ?? null;
  const spineConfidence = official.accountability_confidence ?? scoringData?.confidence ?? null;
  const spineFlags = scoringData?.flags ?? (() => {
    if (!official.accountability_flags) return null;
    try { return JSON.parse(official.accountability_flags); } catch { return null; }
  })();
  const cachedSections = dossier ? Object.keys(dossier.sections).filter(k => dossier.sections[k]?.fresh).length : 0;
  const hasDossier = cachedSections > 0;
  const isArchived = official.lifecycle_status === 'archived';
  const archivedAtLabel = latestArchiveSnapshot?.archived_at ?? official.archived_at ?? null;
  const archiveReasonLabel = latestArchiveSnapshot?.archive_reason ?? official.archive_reason ?? null;
  const snapshotCount = archiveHistory.length;
  const archiveTimeline = buildArchiveHistoryTimeline(archiveHistory, replacedByOfficial);
  const receiptMoments = buildReceiptMoments({
    official,
    accountabilityRecords,
    activity,
  });
  const latestReceiptMoment = receiptMoments[0] ?? null;
  // Skip the moment already promoted to LatestMovementPanel up top, so the
  // receipts lists below don't just repeat the same single record.
  const featuredReceiptMoments = receiptMoments.slice(1, 4);
  const freshestTimestamp = Math.max(
    official.updated_at,
    latestReceiptMoment?.occurredAt ?? 0,
    archiveHistory[0]?.archived_at ?? 0,
  );
  const caseTimeline = buildCaseTimeline({
    official,
    receiptMoments,
    archiveHistory,
    replacedByOfficial,
  });

  const tabs: { id: TabId; label: string; count?: number }[] = [{ id: 'intel', label: 'Intelligence Brief' }];
  if (caseTimeline.length > 0 || featuredReceiptMoments.length > 0) tabs.push({ id: 'timeline', label: 'Exhibit Log', count: caseTimeline.length });
  if (spineScore !== null) tabs.push({ id: 'spine', label: 'Strengthen Your Spine' });
  if (votingRecord && (votingRecord.analysis || votingRecord.votes.length > 0)) tabs.push({ id: 'voting', label: 'Voting Record', count: votingRecord.votes.length });
  if (publicRecord && (publicRecord.text || publicRecord.findings.length > 0)) tabs.push({ id: 'public', label: 'Public Record', count: publicRecord.findings.length });
  if (legislativeRecord && (legislativeRecord.recentActions.length > 0 || legislativeRecord.billsSigned > 0 || legislativeRecord.billsVetoed > 0 || (legislativeRecord.executiveOrders ?? 0) > 0)) tabs.push({ id: 'legislation', label: 'Legislative Record', count: legislativeRecord.recentActions.length });
  if (campaignFinance && (campaignFinance.topContributors.length > 0 || campaignFinance.topSectors.length > 0 || campaignFinance.totalRaised !== null)) tabs.push({ id: 'finance', label: 'Campaign Finance', count: campaignFinance.topContributors.length || campaignFinance.topSectors.length });
  if (allAssets.length > 0) tabs.push({ id: 'sources', label: 'Sources', count: allAssets.length });

  const isEvidenceBoard = caseFileStyle === 'evidence-board';
  const tabsSection = (
    <>
            {/* Tabs — the Submit Contribution trigger now lives in the hero's
                left sidebar, above Adopt, so it isn't repeated here. */}
            {tabs.length > 1 && (
              <div className="overflow-x-auto">
                <TabNav activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />
              </div>
            )}

            {/* Contribution Form */}
            {isAuthenticated && showContributionForm && (
              <ContributionForm
                officialId={official.id}
                officialName={official.name}
                onSuccess={() => setShowContributionForm(false)}
                onCancel={() => setShowContributionForm(false)}
              />
            )}

            {/* INTEL BRIEF TAB */}
            {activeTab === 'intel' && (
              <div className="space-y-4">
                {/* Jump-to nav applies only to the brief's own sections, so it
                    lives inside the Intelligence Brief tab, not above all tabs. */}
                {briefingSections.length > 2 && (
                  <div className="sticky top-0 z-20 bg-cream-50/95 dark:bg-navy-900/95 backdrop-blur-sm border-b border-cream-300 dark:border-navy-600 -mx-4 px-4 py-2.5">
                    <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin">
                      <span className={`text-[9px] font-black uppercase tracking-widest shrink-0 mr-1 ${isEvidenceBoard ? 'text-navy-400 dark:text-navy-300' : 'text-navy-900 mono'}`}>Jump to</span>
                      {briefingSections.filter(s => s.heading).map((section, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            const el = document.getElementById(`section-${i}`);
                            // Sections default collapsed, so expand the target
                            // before scrolling — otherwise Jump To lands on an
                            // empty header with nothing to show.
                            el?.querySelector('button[aria-expanded="false"]')?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          className={isEvidenceBoard
                            ? 'shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-cream-300 dark:border-navy-600 text-navy-500 dark:text-navy-300 bg-white dark:bg-navy-800 hover:border-gold-500 hover:text-gold-600 dark:hover:text-gold-400 transition'
                            : 'shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 mono border border-navy-900 text-navy-900 bg-white hover:bg-navy-900 hover:text-cream-50 transition'
                          }
                        >
                          {section.heading.split('&')[0].trim().slice(0, 25)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {briefingSections.length > 0 ? (
                  briefingSections.map((section, i) => (
                    <div key={i} id={`section-${i}`}>
                      <IntelSectionCard section={section} />
                    </div>
                  ))
                ) : !loadingIntel && !hasDossier ? (
                  <div className="bg-white dark:bg-navy-800/40 border border-dashed border-cream-300 dark:border-navy-600 rounded-xl p-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-cream-200 dark:bg-navy-700 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-navy-400 dark:text-navy-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </div>
                    <p className="text-sm font-bold text-navy-500 dark:text-navy-300 mb-1">No intelligence briefing yet</p>
                    <p className="text-xs text-navy-400 dark:text-navy-200">An administrator needs to run the intelligence agents to build this profile's dossier.</p>
                  </div>
                ) : null}

                {/* Scoring factors */}
                {scoringData?.factors && scoringData.factors.length > 0 && (
                  <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                    <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-4">Key Accountability Factors</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {scoringData.factors.map((f, i) => (
                        <div key={i} className="flex items-start gap-3 bg-cream-100 dark:bg-navy-900/40 rounded-lg p-3">
                          <span className="text-[9px] font-bold text-gold-600 bg-gold-500/10 w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                          <p className="text-xs text-navy-700 dark:text-cream-200 leading-relaxed">{f}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* EXHIBIT LOG TAB */}
            {activeTab === 'timeline' && (
              <div className="space-y-4">
                {/* With only one entry, this just repeats LatestMovementPanel
                    above — only worth showing once there's an actual sequence. */}
                {caseTimeline.length > 1 && (
                  <CaseTimeline entries={caseTimeline} activeAnchorId={activeReceiptAnchorId} />
                )}

                {featuredReceiptMoments.length > 0 && (
                  <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-4">
                      <div>
                        <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Latest Receipts / Evidence</h3>
                        <p className="text-xs text-navy-400 dark:text-navy-300 mt-1">Every shareable claim below maps to a timestamped movement or evidence block on this dossier.</p>
                      </div>
                      {featureFlags.receiptSharing && (
                        <span className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">Receipt sharing enabled</span>
                      )}
                    </div>
                    <div className="space-y-3">
                      {featuredReceiptMoments.map((moment) => (
                        <ReceiptCard
                          key={moment.anchorId}
                          officialId={official.id}
                          officialName={official.name}
                          officeName={official.office_name}
                          moment={moment}
                          shareEnabled={featureFlags.receiptSharing}
                          active={activeReceiptAnchorId === moment.anchorId}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {caseTimeline.length <= 1 && featuredReceiptMoments.length === 0 && (
                  <p className="text-xs text-navy-400 dark:text-navy-300 p-6">No additional exhibits beyond the latest movement shown above yet.</p>
                )}
              </div>
            )}

            {/* STRENGTHEN YOUR SPINE TAB */}
            {activeTab === 'spine' && (
              <div className="space-y-4">
                <SpineRecommendations score={spineScore} factors={scoringData?.factors} officialName={official.name} />
              </div>
            )}

            {/* VOTING RECORD TAB */}
            {activeTab === 'voting' && votingRecord && (
              <div className="space-y-4">
                {votingAnalysisSections.length > 0 && (
                  votingAnalysisSections.map((section, i) => (
                    <IntelSectionCard key={i} section={section} />
                  ))
                )}

                {votingRecord.votes.length > 0 && (
                  <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-cream-300 dark:border-navy-600 flex items-center justify-between">
                      <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Sponsored Legislation</h3>
                      <span className="text-[9px] text-navy-400 dark:text-navy-200">{votingRecord.votes.length} bills</span>
                    </div>
                    <PaginatedTable<CachedVotingRecord['votes'][number]>
                      data={votingRecord.votes}
                      columns={[
                        { key: 'bill', label: 'Bill' },
                        { key: 'title', label: 'Title' },
                        { key: 'date', label: 'Date' },
                        { key: 'category', label: 'Category' },
                      ]}
                      renderRow={(v, i) => (
                        <tr key={i} className="hover:bg-cream-100 dark:hover:bg-navy-700/30 transition">
                          <td data-label="Bill" className="px-4 py-3"><span className="text-xs md:text-[10px] font-bold text-red-400 mono">{v.bill}</span></td>
                          <td data-label="Title" className="px-4 py-3"><span className="text-xs text-navy-700 dark:text-cream-200">{v.title}</span></td>
                          <td data-label="Date" className="px-4 py-3"><span className="text-xs md:text-[10px] text-navy-400 dark:text-navy-300 mono">{v.date}</span></td>
                          <td data-label="Category" className="px-4 py-3">{v.category && <span className="text-xs md:text-[9px] px-2 py-0.5 bg-cream-200 dark:bg-navy-700/80 rounded text-navy-500 dark:text-navy-300">{v.category}</span>}</td>
                        </tr>
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            {/* PUBLIC RECORD TAB */}
            {activeTab === 'public' && publicRecord && (
              <div className="space-y-6">
                {publicRecord.findings.length > 0 && (
                  <div>
                    <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-4">Findings Timeline</h3>
                    <FindingTimeline findings={publicRecord.findings} />
                  </div>
                )}

                {publicRecordSections.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Detailed Analysis</h3>
                    {publicRecordSections.map((section, i) => (
                      <IntelSectionCard key={i} section={section} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'legislation' && legislativeRecord && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricMiniCard label="Bills signed" value={String(legislativeRecord.billsSigned)} />
                  <MetricMiniCard label="Bills vetoed" value={String(legislativeRecord.billsVetoed)} />
                  {legislativeRecord.executiveOrders !== null && (
                    <MetricMiniCard label="Executive orders" value={String(legislativeRecord.executiveOrders)} />
                  )}
                  <MetricMiniCard label="Source" value={legislativeRecord.source.toUpperCase()} />
                </div>

                {/* LegiScan's data is CC BY 4.0 licensed — attribution with a
                    link back is required wherever it's displayed publicly. */}
                {legislativeRecord.source === 'legiscan' && (
                  <p className="text-[10px] text-navy-400 dark:text-navy-300">
                    Legislative data via{' '}
                    <a href="https://legiscan.com" target="_blank" rel="noopener noreferrer" className="text-gold-600 dark:text-gold-400 hover:underline">
                      LegiScan
                    </a>, licensed under{' '}
                    <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className="text-gold-600 dark:text-gold-400 hover:underline">
                      CC BY 4.0
                    </a>.
                  </p>
                )}

                {legislativeRecord.recentActions.length > 0 && (
                  <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-cream-300 dark:border-navy-600">
                      <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Recent legislative actions</h3>
                    </div>
                    <PaginatedTable<CachedLegislativeRecord['recentActions'][number]>
                      data={legislativeRecord.recentActions}
                      columns={[
                        { key: 'billNumber', label: 'Bill' },
                        { key: 'title', label: 'Title' },
                        { key: 'action', label: 'Action' },
                        { key: 'date', label: 'Date' },
                      ]}
                      renderRow={(action, i) => (
                        <tr key={`${action.billNumber}-${i}`} className="hover:bg-cream-100 dark:hover:bg-navy-700/30 transition">
                          <td data-label="Bill" className="px-4 py-3"><span className="text-xs md:text-[10px] font-bold text-red-400 mono">{action.billNumber}</span></td>
                          <td data-label="Title" className="px-4 py-3"><span className="text-xs text-navy-700 dark:text-cream-200">{action.title}</span></td>
                          <td data-label="Action" className="px-4 py-3"><span className="text-xs text-navy-500 dark:text-navy-300">{action.action}</span></td>
                          <td data-label="Date" className="px-4 py-3"><span className="text-xs md:text-[10px] text-navy-400 dark:text-navy-300 mono">{action.date}</span></td>
                        </tr>
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'finance' && campaignFinance && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricMiniCard label="Raised" value={formatCompactCurrency(campaignFinance.totalRaised)} />
                  <MetricMiniCard label="Spent" value={formatCompactCurrency(campaignFinance.totalSpent)} />
                  <MetricMiniCard label="Cash on hand" value={formatCompactCurrency(campaignFinance.cashOnHand)} />
                  <MetricMiniCard label="Debt" value={formatCompactCurrency(campaignFinance.debt)} />
                </div>

                {campaignFinance.topContributors.length > 0 && (
                  <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                    <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-4">Top contributors</h3>
                    <div className="space-y-3">
                      {campaignFinance.topContributors.slice(0, 8).map((contributor) => (
                        <div key={`${contributor.name}-${contributor.total}`} className="flex items-center justify-between gap-4 rounded-lg bg-cream-100 dark:bg-navy-900/40 px-3 py-3">
                          <div>
                            <p className="text-sm font-bold text-navy-900 dark:text-cream-50">{contributor.name}</p>
                            <p className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">{contributor.orgType}</p>
                          </div>
                          <span className="text-sm font-black text-gold-600 dark:text-gold-400">{formatCompactCurrency(contributor.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {campaignFinance.topSectors.length > 0 && (
                  <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                    <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-4">Top sectors</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {campaignFinance.topSectors.slice(0, 8).map((sector) => (
                        <div key={`${sector.sectorId}-${sector.total}`} className="rounded-lg bg-cream-100 dark:bg-navy-900/40 px-3 py-3">
                          <p className="text-sm font-bold text-navy-900 dark:text-cream-50">{sector.sector}</p>
                          <p className="mt-1 text-xs text-navy-500 dark:text-navy-300">{formatCompactCurrency(sector.total)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SOURCES TAB */}
            {activeTab === 'sources' && allAssets.length > 0 && (
              <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-cream-300 dark:border-navy-600">
                  <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Source Audit Log</h3>
                </div>
                <PaginatedTable<AssetAuditRecord>
                  data={allAssets}
                  columns={[
                    { key: 'type', label: 'Type' },
                    { key: 'title', label: 'Source' },
                    { key: 'confidence', label: 'Confidence', className: 'text-center' },
                  ]}
                  renderRow={(asset, i) => (
                    <tr key={`${asset.uri}-${i}`} className="hover:bg-cream-100 dark:hover:bg-navy-700/30 transition group">
                      <td data-label="Type" className="px-4 py-3">
                        <span className={`text-xs md:text-[8px] font-bold px-2 py-1 rounded uppercase ${asset.type === 'GOV_ARCHIVE' ? 'bg-blue-500/10 text-blue-400' : asset.type === 'LEGISLATIVE_RECORD' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                          {asset.type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td data-label="Source" className="px-4 py-3">
                        <a href={asset.uri} target="_blank" rel="noopener noreferrer" className="text-xs text-navy-700 dark:text-cream-200 hover:text-navy-900 dark:hover:text-cream-50 truncate block max-w-md group-hover:underline">{asset.title}</a>
                      </td>
                      <td data-label="Confidence" className="px-4 py-3 text-center">
                        <span className={`text-xs md:text-[9px] font-bold ${asset.confidence === 'HIGH' ? 'text-emerald-500' : asset.confidence === 'MEDIUM' ? 'text-amber-500' : 'text-navy-400 dark:text-navy-300'}`}>{asset.confidence}</span>
                      </td>
                    </tr>
                  )}
                />
              </div>
            )}
    </>
  );


  return (
    <div className={`min-h-screen pb-24 paper-scope ${isEvidenceBoard ? 'bg-kraft-texture' : 'bg-ruled-grid'}`}>
      {/* Hero Header — fixed paper tone (kraft/ruled-grid), same "physical
          document" treatment as the homepage hero: it doesn't invert with
          site dark mode, so text colors inside it are fixed too. Now
          extends the full page height, not just this band, so the whole
          dossier reads as one case file rather than a themed header on a
          stock page. */}
      <div className="relative overflow-hidden border-b border-navy-900/15">
        <div className="relative container mx-auto px-4 pt-8 pb-12">
          <Link to="/officials" className="inline-flex items-center text-[10px] font-bold text-navy-500 uppercase tracking-widest hover:text-red-600 transition mb-8">
            <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back to Database
          </Link>

          {isArchived && (
            <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-bold uppercase tracking-wider text-[10px] text-amber-300">Archived officeholder snapshot</p>
                  <p className="text-amber-50/90">
                    {archivedAtLabel ? `Archived ${new Date(archivedAtLabel).toLocaleDateString()}. ` : ''}
                    {archiveReasonLabel ? `${archiveReasonLabel}. ` : ''}
                    {snapshotCount > 0 ? `${snapshotCount} preserved dossier snapshot${snapshotCount === 1 ? '' : 's'} available.` : 'Frozen dossier snapshot pending.'}
                  </p>
                </div>
                {replacedByOfficial && (
                  <Link to={`/officials/${replacedByOfficial.id}`} className="text-[11px] font-bold uppercase tracking-wider text-amber-300 hover:text-amber-100 transition">
                    View Successor: {replacedByOfficial.name}
                  </Link>
                )}
              </div>
            </div>
          )}

          {isEvidenceBoard ? (
            <>
              <div className="flex items-center gap-2 text-[10px] font-bold text-navy-500 uppercase tracking-[0.2em] mono mb-3">
                <span>Case Files</span>
                <span className="opacity-40">/</span>
                <span>{official.state || 'Federal'}</span>
                <span className="opacity-40">/</span>
                <span className="text-red-600">AAC-{(official.state || 'US').slice(0, 2).toUpperCase()}-{official.id.slice(-4).toUpperCase()}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[280px_1fr_260px] md:items-start bg-white rounded-2xl border border-navy-900/15 shadow-xl overflow-hidden">
                {/* Left: navy sidebar — photo, score, completeness, adopt */}
                <div className="bg-navy-900 p-6 flex flex-col gap-5">
                  <OfficialAvatar
                    name={official.name}
                    photoUrl={official.photo_url}
                    alt={official.name}
                    className="w-full aspect-[4/5] rounded-lg bg-navy-800 border border-navy-700 flex items-center justify-center shrink-0"
                    imgClassName="w-full aspect-[4/5] rounded-lg border border-navy-700 object-cover shrink-0"
                    initialsClassName="text-4xl font-black text-navy-400 uppercase"
                  />
                  <CaseFileScoreCard score={spineScore} confidence={spineConfidence} />
                  <RedFlagBadges flags={spineFlags} />
                  {typeof dossier?.coveragePct === 'number' && (
                    <div className="border-t border-navy-700 pt-4">
                      <div className="font-bold text-[8px] text-navy-300 uppercase tracking-[0.2em] mb-1.5">File completeness</div>
                      <div className="h-[9px] bg-navy-800 border border-navy-700">
                        <div className="h-full bg-gold-500" style={{ width: `${Math.round(dossier.coveragePct)}%` }} />
                      </div>
                      <div className="font-bold text-[9px] text-gold-500 uppercase tracking-[0.1em] mt-1.5">
                        {Math.round(dossier.coveragePct)}%{dossier.missingSections && dossier.missingSections.length > 0 ? ` · ${dossier.missingSections.length} exhibit${dossier.missingSections.length > 1 ? 's' : ''} missing` : ''}
                      </div>
                    </div>
                  )}
                  {isAuthenticated ? (
                    <button
                      onClick={() => setShowContributionForm(!showContributionForm)}
                      className="w-full px-3 py-2.5 min-h-[44px] bg-white hover:bg-cream-100 text-navy-900 text-[10px] font-bold uppercase tracking-wider rounded-lg transition flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>{showContributionForm ? 'Hide Form' : 'Submit Contribution'}</span>
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      className="w-full px-3 py-2.5 min-h-[44px] border border-navy-600 text-navy-200 hover:bg-navy-800 text-[10px] font-bold uppercase tracking-wider rounded-lg transition flex items-center justify-center gap-2"
                      title="Log in to submit a contribution — accounts let us track and verify submissions"
                    >
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      <span>Log In to Contribute</span>
                    </Link>
                  )}
                  <AdoptButton officialId={official.id} initialAdoptionCount={official.adoption_count || 0} />
                  {official.adoption_count > 0 && (
                    <p className="text-center font-bold text-[9px] text-navy-300 uppercase tracking-[0.1em] -mt-3">
                      {official.adoption_count.toLocaleString()} citizen{official.adoption_count === 1 ? '' : 's'} watching
                    </p>
                  )}
                  <div className="border-t border-navy-700 pt-4">
                    <p className="font-bold text-[8px] text-navy-300 uppercase tracking-[0.2em] mb-2">Profile</p>
                    <div className="space-y-1">
                      {official.office_level && <Fact label="Level" value={official.office_level} dark />}
                      {official.office_branch && <Fact label="Branch" value={official.office_branch} dark />}
                      {official.chamber && <Fact label="Chamber" value={official.chamber} dark />}
                      {official.party && <Fact label="Party" value={official.party} dark />}
                      {official.state && <Fact label="State" value={official.state} dark />}
                      {official.district && <Fact label="District" value={official.district} dark />}
                      <Fact label="Status" value={formatOfficialStatusLabel(official)} dark />
                    </div>
                  </div>
                </div>

                {/* Middle: white main panel — name, badges, summary */}
                <div className="p-6 md:p-8 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p className="font-bold text-[9px] text-gold-600 uppercase tracking-[0.24em] mb-2">Subject of Record</p>
                      <h1 className="text-3xl md:text-4xl font-black text-navy-900 tracking-tight leading-[0.95] mb-1">{official.name}</h1>
                      <p className="text-sm text-navy-500 font-medium">{official.office_name}</p>
                    </div>
                    <div className="border-[3px] border-red-600/40 text-red-600/60 px-2.5 py-1.5 rotate-3 font-black text-[9px] uppercase tracking-widest text-center shrink-0">
                      Updated<br />{freshestTimestamp ? new Date(freshestTimestamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${personTypeBadge(official.person_type)}`}>{personTypeLabel(official.person_type)}</span>
                    {official.state && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-cream-300 bg-cream-200 text-navy-500">{official.state}{official.district ? ` - District ${official.district}` : ''}</span>}
                    {countActiveFlags(spineFlags) > 0 && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-red-500/35 bg-red-500/10 text-red-600">
                        {countActiveFlags(spineFlags)} red flag{countActiveFlags(spineFlags) > 1 ? 's' : ''}
                      </span>
                    )}
                    <FreshnessBadge timestamp={freshestTimestamp} compact />
                  </div>

                  {(official.accountability_summary || scoringData?.summary) && (
                    <p className="text-sm text-navy-700 leading-relaxed border-l-2 border-red-600 pl-4 mb-5">{official.accountability_summary || scoringData?.summary}</p>
                  )}

                  {(legislativeRecord || campaignFinance) && (
                    <div className="grid gap-3 md:grid-cols-2 mb-5">
                      {legislativeRecord && <LegislativeSignalCard record={legislativeRecord} />}
                      {campaignFinance && <CampaignFinanceSignalCard finance={campaignFinance} />}
                    </div>
                  )}

                  {latestReceiptMoment && (
                    <LatestMovementPanel
                      officialId={official.id}
                      officialName={official.name}
                      officeName={official.office_name}
                      moment={latestReceiptMoment}
                      shareEnabled={featureFlags.receiptSharing}
                      activeAnchorId={activeReceiptAnchorId}
                    />
                  )}

                  {/* Timeline/Receipts/Votes/Money/Sources tabs live inside the
                      case-file card itself, matching the mockup — not in a
                      separate section further down the page. */}
                  <div className="mt-6 pt-6 border-t border-cream-300">
                    {tabsSection}
                  </div>
                </div>

                {/* Right: pressure it / sources */}
                <div className="bg-cream-100 border-t md:border-t-0 md:border-l border-cream-300 p-5 flex flex-col gap-4">
                  <div>
                    <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Pressure It</p>
                    <div className="flex flex-col gap-2">
                      {official.phone && (
                        <a href={`tel:${official.phone}`} className="bg-navy-900 hover:bg-navy-800 text-cream-50 text-center px-3 py-2.5 font-black text-[10px] uppercase tracking-wide transition">
                          Call the office
                        </a>
                      )}
                      {official.email && (
                        <a href={`mailto:${official.email}`} className="bg-white hover:bg-cream-50 text-navy-900 border border-navy-900 text-center px-3 py-2.5 font-black text-[10px] uppercase tracking-wide transition">
                          Email
                        </a>
                      )}
                      {!official.phone && !official.email && (
                        <p className="text-[10px] text-navy-400 italic">No contact info on file yet.</p>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-cream-300 pt-4">
                    <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Sources · {allAssets.length || dossier?.totalAssets || 0}</p>
                    <p className="text-[11px] text-navy-700 leading-relaxed">
                      {Array.from(new Set(allAssets.map((a) => a.type))).slice(0, 4).join(' · ') || 'No sources pulled yet.'}
                    </p>
                    {tabs.some((t) => t.id === 'sources') && (
                      <button
                        onClick={() => setActiveTab('sources')}
                        className="mt-2 font-bold text-[9px] text-gold-600 uppercase tracking-wide hover:underline"
                      >
                        See the trail →
                      </button>
                    )}
                  </div>
                  <div className="border-t border-cream-300 pt-4">
                    <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Data Source</p>
                    <p className="text-[11px] text-navy-700 font-medium">
                      {official.source === 'congress_gov' ? 'Congress.gov API' : official.source === 'openstates' ? 'OpenStates Bulk Data' : official.source === 'manual' ? 'Manual Entry' : official.source}
                    </p>
                    <p className="text-[9px] text-navy-400 mono mt-1">Refreshed {new Date(official.updated_at).toLocaleDateString()}</p>
                    {official.bioguide_id && <p className="text-[9px] text-navy-400 mono mt-0.5">Bioguide: {official.bioguide_id}</p>}
                  </div>
                  <div className="border-t border-cream-300 pt-4">
                    <ShareButtons official={official} compact />
                  </div>
                  {blueskyThread?.hasPost && (
                    <div className="border-t border-cream-300 pt-4 -mx-5 px-5">
                      <BlueskyEngagement data={blueskyThread} />
                    </div>
                  )}
                  {(official.phone || official.email || official.website) && (
                    <div className="border-t border-cream-300 pt-4">
                      <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Contact</p>
                      <div className="space-y-2">
                        {official.phone && (
                          <a href={`tel:${official.phone}`} className="flex items-center gap-2 text-xs text-navy-900 hover:text-red-500 transition">
                            <svg className="w-3.5 h-3.5 text-navy-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                            <span className="mono">{official.phone}</span>
                          </a>
                        )}
                        {official.email && (
                          <a href={`mailto:${official.email}`} className="flex items-center gap-2 text-xs text-navy-900 hover:text-red-500 transition break-all">
                            <svg className="w-3.5 h-3.5 text-navy-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            <span>{official.email}</span>
                          </a>
                        )}
                        {official.website && (
                          <a href={official.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-navy-900 hover:text-red-500 transition break-all">
                            <svg className="w-3.5 h-3.5 text-navy-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></svg>
                            <span>Website</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {official.office_address && (
                    <div className="border-t border-cream-300 pt-4">
                      <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Office</p>
                      <p className="text-xs text-navy-700">{official.office_address}</p>
                    </div>
                  )}
                  {socialAccounts.length > 0 && (
                    <div className="border-t border-cream-300 pt-4">
                      <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Social</p>
                      <div className="space-y-2">
                        {socialAccounts.map((acc) => (
                          <a key={acc.id} href={acc.url || undefined} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-navy-900 hover:text-red-500 transition">
                            <span className="w-5 h-5 rounded bg-cream-200 flex items-center justify-center text-[7px] font-bold text-navy-500 uppercase shrink-0">{platformIcon(acc.platform)}</span>
                            <span className="truncate">{acc.handle}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
          <div className="bg-cream-50 rounded-2xl border-2 border-navy-900 shadow-xl overflow-hidden">
            {/* Record header — navy bar with score + adopt, matching the
                field-report direction's "accountability file" framing. */}
            <div className="bg-navy-900 px-6 md:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-bold text-[9px] text-navy-300 uppercase tracking-[0.2em] mono mb-1.5">
                  Accountability File · AAC-{(official.state || 'US').slice(0, 2).toUpperCase()}-{official.id.slice(-4).toUpperCase()}
                  {official.created_at ? ` · Opened ${new Date(official.created_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                </p>
                <h1 className="text-2xl md:text-3xl font-black text-cream-50 uppercase tracking-tight leading-none">{official.name}</h1>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="flex items-baseline gap-2 justify-end">
                    <span className="text-3xl font-black text-cream-50 leading-none">{spineScore ?? '--'}</span>
                    {spineScore !== null && (
                      <span
                        className="font-black text-[10px] text-cream-50 px-1.5 py-0.5 uppercase tracking-widest"
                        style={{ backgroundColor: scoreTier(spineScore).color }}
                      >
                        {scoreTier(spineScore).label}
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-[8px] text-navy-300 uppercase tracking-[0.16em] mt-0.5">Spine score</div>
                </div>
                <div className="[&_button]:bg-gold-500 [&_button:hover]:bg-gold-400 [&_button]:text-navy-900">
                  <AdoptButton officialId={official.id} initialAdoptionCount={official.adoption_count || 0} />
                </div>
              </div>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-cream-300 border-b border-cream-300 bg-white">
              <div className="px-4 py-3">
                <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.16em] mb-1">Office</p>
                <p className="font-bold text-sm text-navy-900">{official.office_name}</p>
              </div>
              <div className="px-4 py-3">
                <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.16em] mb-1">Jurisdiction</p>
                <p className="font-bold text-sm text-navy-900">{official.state || 'Federal'}{official.district ? ` · District ${official.district}` : ''}</p>
              </div>
              <div className="px-4 py-3">
                <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.16em] mb-1">Red Flags</p>
                <p className={`font-bold text-sm ${countActiveFlags(spineFlags) > 0 ? 'text-red-600' : 'text-navy-900'}`}>{countActiveFlags(spineFlags)} active</p>
              </div>
              <div className="px-4 py-3">
                <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.16em] mb-1">Sources</p>
                <p className="font-bold text-sm text-navy-900">{allAssets.length || dossier?.totalAssets || 0} verified</p>
              </div>
              <div className="px-4 py-3">
                <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.16em] mb-1">Last Updated</p>
                <p className="font-bold text-sm text-navy-900">{freshestTimestamp ? new Date(freshestTimestamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—'}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] md:items-start">
              {/* Left: photo, file completeness, actions */}
              <div className="bg-white border-b md:border-b-0 md:border-r border-cream-300 p-5 flex flex-col gap-5">
                <OfficialAvatar
                  name={official.name}
                  photoUrl={official.photo_url}
                  alt={official.name}
                  className="w-full aspect-[4/5] rounded bg-cream-200 border border-cream-300 flex items-center justify-center shrink-0"
                  imgClassName="w-full aspect-[4/5] rounded border border-cream-300 object-cover shrink-0"
                  initialsClassName="text-3xl font-black text-navy-300 uppercase"
                />

                {dossier && (
                  <div>
                    <p className="font-bold text-[9px] text-navy-400 uppercase tracking-[0.16em] mb-2">File completeness</p>
                    <div className="space-y-1.5 mb-2">
                      {(dossier.expectedSections ?? Object.keys(dossier.sections))
                        .filter((key) => SECTION_LABELS[key])
                        .map((key) => {
                          const done = Boolean(dossier.sections[key]?.cached);
                          return (
                            <div key={key} className="flex items-center justify-between text-[11px]">
                              <span className={done ? 'text-navy-900 font-semibold' : 'text-navy-400'}>{SECTION_LABELS[key]}</span>
                              <span className={done ? 'text-emerald-600' : 'text-navy-300'}>{done ? '✓' : '—'}</span>
                            </div>
                          );
                        })}
                    </div>
                    {typeof dossier.coveragePct === 'number' && (
                      <>
                        <div className="h-[7px] bg-cream-200 border border-cream-300">
                          <div className="h-full bg-navy-900" style={{ width: `${Math.round(dossier.coveragePct)}%` }} />
                        </div>
                        <p className="font-bold text-[9px] text-navy-500 uppercase tracking-[0.1em] mt-1.5">{Math.round(dossier.coveragePct)}% · Help us close it</p>
                      </>
                    )}
                  </div>
                )}

                <div className="border-t border-cream-300 pt-4">
                  <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Act on this file</p>
                  <div className="flex flex-col gap-2">
                    {official.phone && (
                      <a href={`tel:${official.phone}`} className="bg-navy-900 hover:bg-navy-800 text-cream-50 text-center px-3 py-2.5 font-black text-[10px] uppercase tracking-wide transition">
                        Call {official.phone}
                      </a>
                    )}
                    {official.email && (
                      <a href={`mailto:${official.email}`} className="bg-white hover:bg-cream-50 text-navy-900 border border-navy-900 text-center px-3 py-2.5 font-black text-[10px] uppercase tracking-wide transition">
                        Email with script
                      </a>
                    )}
                    {!official.phone && !official.email && (
                      <p className="text-[10px] text-navy-400 italic">No contact info on file yet.</p>
                    )}
                    {isAuthenticated ? (
                      <button
                        onClick={() => setShowContributionForm(!showContributionForm)}
                        className="bg-navy-900 hover:bg-navy-800 text-cream-50 text-center px-3 py-2.5 font-black text-[10px] uppercase tracking-wide transition"
                      >
                        {showContributionForm ? 'Hide Form' : '+ Submit Contribution'}
                      </button>
                    ) : (
                      <Link
                        to="/login"
                        className="bg-white hover:bg-cream-50 text-navy-900 border border-navy-900 text-center px-3 py-2.5 font-black text-[10px] uppercase tracking-wide transition"
                        title="Log in to submit a contribution — accounts let us track and verify submissions"
                      >
                        Log In to Contribute
                      </Link>
                    )}
                  </div>
                </div>

                <div className="border-t border-cream-300 pt-4">
                  <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Sources · {allAssets.length || dossier?.totalAssets || 0}</p>
                  <p className="text-[11px] text-navy-700 leading-relaxed">
                    {Array.from(new Set(allAssets.map((a) => a.type))).slice(0, 4).join(' · ') || 'No sources pulled yet.'}
                  </p>
                  {tabs.some((t) => t.id === 'sources') && (
                    <button
                      onClick={() => setActiveTab('sources')}
                      className="mt-2 font-bold text-[9px] text-gold-600 uppercase tracking-wide hover:underline"
                    >
                      See the trail →
                    </button>
                  )}
                </div>

                <div className="border-t border-cream-300 pt-4">
                  <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Data Source</p>
                  <p className="text-[11px] text-navy-700 font-medium">
                    {official.source === 'congress_gov' ? 'Congress.gov API' : official.source === 'openstates' ? 'OpenStates Bulk Data' : official.source === 'manual' ? 'Manual Entry' : official.source}
                  </p>
                  <p className="text-[9px] text-navy-400 mono mt-1">Refreshed {new Date(official.updated_at).toLocaleDateString()}</p>
                  {official.bioguide_id && <p className="text-[9px] text-navy-400 mono mt-0.5">Bioguide: {official.bioguide_id}</p>}
                </div>

                <div className="border-t border-cream-300 pt-4">
                  <ShareButtons official={official} compact />
                </div>

                <div className="border-t border-cream-300 pt-4">
                  <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Profile</p>
                  <div className="space-y-1">
                    {official.office_level && <Fact label="Level" value={official.office_level} />}
                    {official.office_branch && <Fact label="Branch" value={official.office_branch} />}
                    {official.chamber && <Fact label="Chamber" value={official.chamber} />}
                    {official.party && <Fact label="Party" value={official.party} />}
                    {official.state && <Fact label="State" value={official.state} />}
                    {official.district && <Fact label="District" value={official.district} />}
                    <Fact label="Status" value={formatOfficialStatusLabel(official)} />
                  </div>
                </div>

                {official.office_address && (
                  <div className="border-t border-cream-300 pt-4">
                    <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Office</p>
                    <p className="text-xs text-navy-700">{official.office_address}</p>
                  </div>
                )}

                {socialAccounts.length > 0 && (
                  <div className="border-t border-cream-300 pt-4">
                    <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Social</p>
                    <div className="space-y-2">
                      {socialAccounts.map((acc) => (
                        <a key={acc.id} href={acc.url || undefined} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-navy-900 hover:text-red-500 transition">
                          <span className="w-5 h-5 rounded bg-cream-200 flex items-center justify-center text-[7px] font-bold text-navy-500 uppercase shrink-0">{platformIcon(acc.platform)}</span>
                          <span className="truncate">{acc.handle}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {blueskyThread?.hasPost && (
                  <div className="border-t border-cream-300 pt-4 -mx-5 px-5">
                    <BlueskyEngagement data={blueskyThread} />
                  </div>
                )}
              </div>

              {/* Right: tabs + record content */}
              <div className="p-5 md:p-7 min-w-0">
                {(official.accountability_summary || scoringData?.summary) && (
                  <p className="text-sm text-navy-700 leading-relaxed border-l-2 border-red-600 pl-4 mb-5">{official.accountability_summary || scoringData?.summary}</p>
                )}
                {(legislativeRecord || campaignFinance) && (
                  <div className="grid gap-3 md:grid-cols-2 mb-5">
                    {legislativeRecord && <LegislativeSignalCard record={legislativeRecord} />}
                    {campaignFinance && <CampaignFinanceSignalCard finance={campaignFinance} />}
                  </div>
                )}
                {latestReceiptMoment && (
                  <LatestMovementPanel
                    officialId={official.id}
                    officialName={official.name}
                    officeName={official.office_name}
                    moment={latestReceiptMoment}
                    shareEnabled={featureFlags.receiptSharing}
                    activeAnchorId={activeReceiptAnchorId}
                  />
                )}
                <div className="mt-6 pt-6 border-t border-cream-300">
                  {tabsSection}
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* The old full-width "Dashboard Stats Strip" is gone: evidence-board's
          Sources already lives in its hero card's right column, and
          field-report's hero card now has its own Office/Jurisdiction/Red
          Flags/Sources/Last Updated stat row, so a separate strip would
          just duplicate both. */}
      <div className="container mx-auto px-4 mt-6">
        {/* Both directions now embed the tabs/timeline content inside their
            own hero card above, so this is supplementary detail only. Full-
            width masonry-style grid, not a narrow sidebar pinned to one
            side — there's no competing main column left to share space
            with, so a right-pinned narrow column just wasted the rest of
            the page. */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-start">
            {/* Spine Score Share Card — evidence-board already shows the score
                card in the hero's left sidebar, so skip the duplicate. */}
            {false && spineScore !== null && (
              <SpineShareCard score={spineScore} officialName={official.name} officeName={official.office_name} party={official.party} />
            )}

            {/* Share — evidence-board already surfaces this in the hero's
                sidebar next to Pressure It, so skip the duplicate below. */}
            {false && <ShareButtons official={official} />}

            {featureFlags.receiptSharing && featuredReceiptMoments.length > 0 && (
              <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5 space-y-3">
                <div>
                  <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Shareable Receipts</h3>
                  <p className="text-xs text-navy-400 dark:text-navy-300 mt-1">Deep-link clean accountability moments without outrunning the evidence on this page.</p>
                </div>
                {featuredReceiptMoments.map((moment) => (
                  <ReceiptCard
                    key={moment.anchorId}
                    officialId={official.id}
                    officialName={official.name}
                    officeName={official.office_name}
                    moment={moment}
                    shareEnabled
                    compact
                    active={activeReceiptAnchorId === moment.anchorId}
                    anchorable={false}
                    shareVariants={[
                      { label: 'Copy receipt', text: `${official.name} — ${official.office_name}\n${moment.claim}\n${moment.summary}` },
                      { label: 'Copy verdict', text: `${official.name} accountability check: ${official.accountability_summary || scoringData?.summary || moment.claim}` },
                    ]}
                  />
                ))}
              </div>
            )}

            {/* Quick Facts — both hero directions now show this in their own
                left sidebar, so skip the duplicate. */}
            {false && (
              <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-4">Profile</h3>
                <div className="space-y-2.5">
                  {official.office_level && <Fact label="Level" value={official.office_level} />}
                  {official.office_branch && <Fact label="Branch" value={official.office_branch} />}
                  {official.chamber && <Fact label="Chamber" value={official.chamber} />}
                  {official.party && <Fact label="Party" value={official.party} />}
                  {official.state && <Fact label="State" value={official.state} />}
                  {official.district && <Fact label="District" value={official.district} />}
                  <Fact label="Status" value={formatOfficialStatusLabel(official)} />
                </div>
              </div>
            )}

            {(archiveTimeline.length > 0 || replacedByOfficial || isArchived) && (
              <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Archive History</h3>
                    <p className="text-xs text-navy-400 dark:text-navy-300 mt-1">
                      {isArchived ? 'Public lifecycle record for this archived officeholder.' : 'Public lifecycle record for leadership turnover and prior snapshots.'}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-cream-300 dark:border-navy-600 text-navy-500 dark:text-navy-300">
                    {snapshotCount} snapshot{snapshotCount === 1 ? '' : 's'}
                  </span>
                </div>
                {replacedByOfficial && (
                  <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Successor on record</p>
                    <Link to={`/officials/${replacedByOfficial.id}`} className="mt-1 inline-flex text-sm font-semibold text-navy-900 dark:text-cream-50 hover:text-red-500 transition">
                      {replacedByOfficial.name}
                    </Link>
                    <p className="text-xs text-navy-400 dark:text-navy-300 mt-1">{replacedByOfficial.office_name}{replacedByOfficial.state ? ` • ${replacedByOfficial.state}` : ''}</p>
                  </div>
                )}
                {archiveTimeline.length > 0 ? (
                  <div className="space-y-3">
                    {archiveTimeline.slice(0, 5).map((item) => (
                      <div key={item.id} className="rounded-lg border border-cream-200 dark:border-navy-600/40 bg-cream-50 dark:bg-navy-900/30 px-3 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-bold text-navy-900 dark:text-cream-50">{item.dateLabel}</p>
                            <p className="text-[10px] uppercase tracking-wider text-navy-400 dark:text-navy-300 mt-1">{item.reasonLabel}</p>
                            {item.successorName && item.successorId && (
                              <Link to={`/officials/${item.successorId}`} className="mt-2 inline-flex text-xs font-semibold text-gold-600 dark:text-gold-400 hover:text-navy-900 dark:hover:text-cream-50 transition">
                                Successor: {item.successorName}
                              </Link>
                            )}
                          </div>
                          {item.isLatest && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                              Latest
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-navy-400 dark:text-navy-300">No archived snapshots have been preserved yet.</p>
                )}
              </div>
            )}

            {/* Contact — evidence-board already shows this in the hero's
                right sidebar under Share This Dossier, so skip the duplicate. */}
            {false && (official.phone || official.email || official.website) && (
              <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-4">Contact</h3>
                <div className="space-y-3">
                  {official.phone && (
                    <a href={`tel:${official.phone}`} className="flex items-center gap-3 text-sm text-navy-900 dark:text-cream-50 hover:text-red-400 transition">
                      <svg className="w-4 h-4 text-navy-400 dark:text-navy-200 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      <span className="mono text-sm">{official.phone}</span>
                    </a>
                  )}
                  {official.email && (
                    <a href={`mailto:${official.email}`} className="flex items-center gap-3 text-sm text-navy-900 dark:text-cream-50 hover:text-red-400 transition break-all">
                      <svg className="w-4 h-4 text-navy-400 dark:text-navy-200 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      <span className="text-sm">{official.email}</span>
                    </a>
                  )}
                  {official.website && (
                    <a href={official.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-gold-600 dark:text-gold-400 hover:text-navy-900 dark:text-cream-50 transition">
                      <svg className="w-4 h-4 text-navy-400 dark:text-navy-200 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      <span className="text-sm truncate">{official.website.replace(/^https?:\/\//, '')}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Office Info — both hero directions now show this in their own
                sidebar instead. */}
            {false && official.office_address && (
              <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
                <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-3">Office</h3>
                <p className="text-sm text-navy-700 dark:text-cream-200">{official.office_address}</p>
              </div>
            )}

            {/* Social Accounts — both hero directions now show this in their
                own sidebar instead. */}
            {false && socialAccounts.length > 0 && (
              <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-cream-200 dark:border-navy-600/40">
                  <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Social</h3>
                </div>
                <div className="divide-y divide-slate-800/30">
                  {socialAccounts.map((acc) => (
                    <div key={acc.id} className="px-5 py-3 flex items-center justify-between hover:bg-cream-100 dark:hover:bg-navy-700/30 transition">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded bg-cream-200 dark:bg-navy-700 flex items-center justify-center text-[8px] font-bold text-navy-500 dark:text-navy-300 uppercase">{platformIcon(acc.platform)}</span>
                        <div>
                          <p className="text-sm font-medium text-navy-900 dark:text-cream-50">{acc.handle}</p>
                          <p className="text-[9px] text-navy-400 dark:text-navy-300 uppercase">{acc.platform}</p>
                        </div>
                      </div>
                      {acc.url && <a href={acc.url} target="_blank" rel="noopener noreferrer" className="text-[9px] font-bold text-gold-600 dark:text-gold-400 hover:text-navy-900 dark:text-cream-50 uppercase transition">Visit</a>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bluesky Discussion — evidence-board shows this in the hero's
                right sidebar under Share This Dossier instead. */}
            {false && <BlueskyEngagement data={blueskyThread} />}

            {/* Spine Graphic — redundant with the score already shown in the
                hero for evidence-board (card) and field-report (header bar). */}
            {false && <SpineGraphic score={spineScore} />}

            {/* Data Source — both hero directions now show this in their own
                sidebar instead. */}
            {false && (
              <div id="receipt-dossier-refresh" className="bg-cream-50 dark:bg-navy-800/20 border border-cream-200 dark:border-navy-600/40 rounded-xl p-5 space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-200 uppercase tracking-widest mb-2">Data Source</h3>
                    <p className="text-xs text-navy-400 dark:text-navy-300 font-medium">
                      {official.source === 'congress_gov' ? 'Congress.gov API' : official.source === 'openstates' ? 'OpenStates Bulk Data' : official.source === 'manual' ? 'Manual Entry' : official.source}
                    </p>
                    <p className="text-[9px] text-navy-400 dark:text-navy-200 mono mt-1">Dossier refreshed {new Date(official.updated_at).toLocaleString()}</p>
                    {latestReceiptMoment && <p className="text-[9px] text-navy-400 dark:text-navy-200 mono mt-1">Latest movement at {new Date(latestReceiptMoment.occurredAt).toLocaleString()}</p>}
                    {official.bioguide_id && <p className="text-[9px] text-navy-400 dark:text-navy-200 mono mt-1">Bioguide: {official.bioguide_id}</p>}
                  </div>
                  <FreshnessBadge timestamp={freshestTimestamp} />
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Spine Graphic
// ---------------------------------------------------------------------------

const LegislativeSignalCard: React.FC<{ record: CachedLegislativeRecord }> = ({ record }) => (
  <div className="rounded-2xl border border-cream-300 bg-white p-4">
    <p className="text-[9px] font-black uppercase tracking-widest text-gold-600">Legislative record</p>
    <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-widest text-navy-400">
      <span>{record.billsSigned} signed</span>
      <span>{record.billsVetoed} vetoed</span>
      {record.executiveOrders !== null && <span>{record.executiveOrders} orders</span>}
    </div>
    {record.recentActions[0] && (
      <p className="mt-3 text-sm text-navy-700 leading-relaxed">
        Latest action: {record.recentActions[0].billNumber} — {record.recentActions[0].action}.
      </p>
    )}
  </div>
);

const CampaignFinanceSignalCard: React.FC<{ finance: CachedCampaignFinance }> = ({ finance }) => (
  <div className="rounded-2xl border border-cream-300 bg-white p-4">
    <p className="text-[9px] font-black uppercase tracking-widest text-gold-600">Campaign finance</p>
    <div className="mt-3 grid grid-cols-2 gap-2 text-sm font-black text-navy-900">
      <span>{formatCompactCurrency(finance.totalRaised)}</span>
      <span>{formatCompactCurrency(finance.cashOnHand)}</span>
    </div>
    <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-navy-400">Raised / cash on hand</p>
    {finance.topContributors[0] && (
      <p className="mt-3 text-sm text-navy-700 leading-relaxed">
        Top contributor on record: {finance.topContributors[0].name} at {formatCompactCurrency(finance.topContributors[0].total)}.
      </p>
    )}
  </div>
);

const MetricMiniCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl border border-cream-300 dark:border-navy-600 bg-white dark:bg-navy-800/40 px-4 py-3">
    <p className="text-[9px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">{label}</p>
    <p className="mt-2 text-sm font-black text-navy-900 dark:text-cream-50">{value}</p>
  </div>
);

const formatCompactCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: Math.abs(value) >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(value) >= 1000 ? 1 : 0,
  }).format(value);
};

const SpineGraphic: React.FC<{ score: number | null }> = ({ score }) => {
  const { style } = useCaseFileStyle();
  const isReport = style === 'field-report';
  const s = score !== null && score !== undefined ? score : 50;
  const segments = 20;
  const active = Math.ceil((s / 100) * segments);
  const label = s >= 70 ? 'Strong backbone' : s >= 40 ? 'Needs stiffening' : 'Spineless';
  const color = s >= 70 ? 'green' : s >= 40 ? 'yellow' : 'red';
  const colors = { green: { seg: 'bg-emerald-500', glow: 'shadow-emerald-500/30', dot: 'bg-emerald-300', label: 'text-emerald-400' }, yellow: { seg: 'bg-amber-500', glow: 'shadow-amber-500/30', dot: 'bg-amber-300', label: 'text-amber-400' }, red: { seg: 'bg-red-500', glow: 'shadow-red-500/30', dot: 'bg-red-300', label: 'text-red-400' } }[color];

  return (
    <div className={isReport
      ? 'flex flex-col items-center p-5 bg-white border border-navy-900'
      : 'flex flex-col items-center p-5 bg-cream-50 dark:bg-navy-800/30 rounded-xl border border-cream-200 dark:border-navy-600/40'
    }>
      <span className={`text-[8px] font-bold uppercase tracking-widest mb-2 ${isReport ? 'text-navy-900 mono' : 'text-navy-400 dark:text-navy-200'}`}>Spine Check</span>
      <span className={`text-[9px] font-bold uppercase tracking-wider mb-3 ${colors.label}`}>{label}</span>
      <div className="flex flex-col-reverse gap-0.5">
        {Array.from({ length: segments }).map((_, i) => {
          const isActive = i < active;
          return (
            <div key={i} className={`w-10 h-2 rounded-sm flex items-center justify-between px-0.5 transition-all spine-segment ${isActive ? `${colors.seg} shadow-sm ${colors.glow}` : 'bg-white dark:bg-navy-800 border border-slate-800/20'}`} style={{ opacity: isActive ? 1 : 0.12 }}>
              <div className={`w-0.5 h-0.5 rounded-full ${isActive ? 'bg-white' : 'bg-slate-700'}`} />
              <div className={`w-1 h-1 rounded-sm ${isActive ? colors.dot : 'bg-cream-200 dark:bg-navy-700'}`} />
              <div className={`w-0.5 h-0.5 rounded-full ${isActive ? 'bg-white' : 'bg-slate-700'}`} />
            </div>
          );
        })}
      </div>
      <span className="text-[9px] font-bold text-navy-400 dark:text-navy-200 mono mt-3">{s}%</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center gap-2 shrink-0">
    <span className="text-sm font-bold text-navy-900">{value}</span>
    <span className="text-[9px] font-bold text-navy-400 uppercase tracking-wider">{label}</span>
  </div>
);

const Fact: React.FC<{ label: string; value: string; dark?: boolean }> = ({ label, value, dark = false }) => (
  <div className="flex flex-col gap-0.5 py-1">
    <span className={`text-[9px] font-bold uppercase tracking-wider ${dark ? 'text-navy-300' : 'text-navy-400 dark:text-navy-200'}`}>{label}</span>
    <span className={`text-xs font-medium capitalize ${dark ? 'text-cream-100' : 'text-navy-700 dark:text-cream-200'}`}>{value}</span>
  </div>
);

// --- Share Buttons ---

function ShareButtons({ official, compact = false }: { official: Official; compact?: boolean }) {
  const [copied, setCopied] = useState(false);

  const baseUrl = `${window.location.origin}/officials/${official.id}`;
  const makeUrl = (source: string) => `${baseUrl}?utm_source=${source}&utm_medium=social&utm_campaign=dossier_share`;
  const shareText = `Accountability dossier on ${official.name}${official.office_name ? ` — ${official.office_name}` : ''}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(makeUrl('clipboard'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = makeUrl('clipboard');
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const buttons = [
    {
      label: 'X',
      href: `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(makeUrl('twitter'))}`,
      icon: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />,
    },
    {
      label: 'Reddit',
      href: `https://reddit.com/submit?url=${encodeURIComponent(makeUrl('reddit'))}&title=${encodeURIComponent(shareText)}`,
      icon: <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.8 11.33c.02.16.03.33.03.5 0 2.55-2.97 4.63-6.63 4.63-3.65 0-6.62-2.07-6.62-4.63 0-.17.01-.33.03-.5a1.4 1.4 0 01-.53-1.1c0-.78.63-1.4 1.4-1.4.38 0 .72.15.97.39 1.15-.8 2.7-1.3 4.42-1.35l.88-4.14a.3.3 0 01.36-.24l2.9.62a1 1 0 11-.16.74l-2.57-.55-.78 3.67c1.7.06 3.22.56 4.36 1.35.25-.25.6-.39.97-.39.78 0 1.4.63 1.4 1.4 0 .44-.2.83-.52 1.1zM8.5 13.5a1 1 0 100 2 1 1 0 000-2zm7 0a1 1 0 100 2 1 1 0 000-2zm-3.5 4a3.4 3.4 0 002.7-1.35.3.3 0 00-.45-.4c-.54.44-1.38.75-2.25.75s-1.7-.3-2.25-.75a.3.3 0 00-.44.4A3.4 3.4 0 0012 17.5z" />,
    },
    {
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(makeUrl('facebook'))}`,
      icon: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />,
    },
  ];

  if (compact) {
    // Icon-only row for the hero sidebar (next to Pressure It) — the full
    // labeled-button card is redundant once this is already up top.
    return (
      <div>
        <p className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-2.5">Share This Dossier</p>
        <div className="flex flex-wrap gap-2">
          {buttons.map(b => (
            <a
              key={b.label}
              href={b.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-lg bg-cream-200 hover:bg-navy-800 flex items-center justify-center text-navy-500 hover:text-white transition"
              title={`Share on ${b.label}`}
            >
              <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 24 24">{b.icon}</svg>
            </a>
          ))}
          <button
            onClick={handleCopy}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition ${
              copied ? 'bg-green-500/20 text-green-600' : 'bg-cream-200 hover:bg-navy-800 text-navy-500 hover:text-white'
            }`}
            title={copied ? 'Copied!' : 'Copy link'}
          >
            {copied ? (
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            ) : (
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
      <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-3">Share This Dossier</h3>
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
        {buttons.map(b => (
          <a
            key={b.label}
            href={b.href}
            target="_blank"
            rel="noopener noreferrer"
            className="min-h-[48px] px-4 py-2 rounded-lg bg-cream-200 dark:bg-navy-700 hover:bg-navy-800 dark:hover:bg-gold-500 flex items-center justify-center gap-2 text-navy-500 dark:text-navy-300 hover:text-white dark:hover:text-navy-900 transition"
            title={`Share on ${b.label}`}
          >
            <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24">{b.icon}</svg>
            <span className="text-xs font-bold">{b.label}</span>
          </a>
        ))}
        <button
          onClick={handleCopy}
          className={`min-h-[48px] px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition ${
            copied
              ? 'bg-green-500/20 text-green-400'
              : 'bg-cream-200 dark:bg-navy-700 hover:bg-navy-800 dark:hover:bg-gold-500 text-navy-500 dark:text-navy-300 hover:text-white dark:hover:text-navy-900'
          }`}
          title={copied ? 'Copied!' : 'Copy link'}
        >
          {copied ? (
            <>
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              <span className="text-xs font-bold">Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              <span className="text-xs font-bold">Copy</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// --- Bluesky Engagement ---

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

function ReplyThread({ reply, depth = 0 }: { reply: BlueskyReply; depth?: number; key?: React.Key }) {
  const replyUrl = reply.uri
    ? (() => {
        const m = reply.uri.match(/^at:\/\/(did:[^/]+)\/app\.bsky\.feed\.post\/(.+)$/);
        return m ? `https://bsky.app/profile/${m[1]}/post/${m[2]}` : null;
      })()
    : null;

  return (
    <div className={depth > 0 ? 'ml-4 pl-3 border-l-2 border-cream-200 dark:border-navy-600/60' : ''}>
      <div className="py-2.5">
        <div className="flex items-start gap-2.5">
          {reply.avatar ? (
            <img src={reply.avatar} alt="" className="w-7 h-7 rounded-full shrink-0 mt-0.5" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-blue-500/20 shrink-0 mt-0.5 flex items-center justify-center">
              <span className="text-[9px] font-bold text-blue-400">{(reply.displayName || reply.handle)[0]?.toUpperCase()}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-xs font-bold text-navy-900 dark:text-cream-50 truncate">{reply.displayName}</span>
              <span className="text-[9px] text-navy-400 dark:text-navy-300 truncate">@{reply.handle}</span>
              {reply.createdAt && <span className="text-[9px] text-navy-400 dark:text-navy-400">{timeAgo(reply.createdAt)}</span>}
            </div>
            <p className="text-xs text-navy-700 dark:text-cream-200 leading-relaxed mt-0.5 whitespace-pre-wrap break-words">{reply.text}</p>
            <div className="flex items-center gap-3 mt-1">
              {reply.likeCount > 0 && (
                <span className="text-[9px] text-navy-400 dark:text-navy-300 flex items-center gap-0.5">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                  {reply.likeCount}
                </span>
              )}
              {replyUrl && (
                <a href={replyUrl} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-400 hover:text-blue-300 transition">
                  view
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      {reply.replies && reply.replies.length > 0 && (
        <div>
          {reply.replies.slice(0, 3).map((nested, i) => (
            <ReplyThread key={i} reply={nested} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function BlueskyEngagement({ data }: { data: BlueskyThreadData | null }) {
  const { style } = useCaseFileStyle();
  const isReport = style === 'field-report';
  if (!data || !data.hasPost) return null;

  const hasReplies = data.topReplies && data.topReplies.length > 0;

  return (
    <div className={isReport
      ? 'bg-white border border-navy-900 overflow-hidden'
      : 'bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden'
    }>
      <div className={isReport ? 'px-5 py-3 bg-cream-100 flex items-center gap-2' : 'px-5 py-3 border-b border-cream-200 dark:border-navy-600/40 flex items-center gap-2'}>
        <svg className="w-4 h-4 text-blue-400" viewBox="0 0 568 501" fill="currentColor">
          <path d="M123.121 33.664C188.241 82.553 258.281 181.68 284 234.873c25.719-53.192 95.759-152.32 160.879-201.21C491.866-1.611 568-28.906 568 57.947c0 17.346-9.945 145.713-15.778 166.555-20.275 72.453-94.155 90.933-159.875 79.748C507.222 323.8 536.444 388.56 473.333 453.32c-119.86 122.992-172.272-30.859-185.702-70.281-2.462-7.227-3.614-10.608-3.631-7.733-.017-2.875-1.169.506-3.631 7.733-13.43 39.422-65.842 193.273-185.702 70.281-63.111-64.76-33.89-129.52 80.986-149.07-65.72 11.185-139.6-7.295-159.875-79.748C9.945 203.659 0 75.291 0 57.946 0-28.906 76.135-1.612 123.121 33.664z" />
        </svg>
        <h3 className={`text-[10px] font-bold uppercase tracking-widest ${isReport ? 'text-navy-900 mono' : 'text-navy-400 dark:text-navy-300'}`}>Join the Discussion</h3>
      </div>
      <div className="p-5">
        {/* Engagement Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <p className="text-lg font-black text-navy-900 dark:text-cream-50">{data.likes ?? 0}</p>
            <p className="text-[8px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Likes</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-navy-900 dark:text-cream-50">{data.reposts ?? 0}</p>
            <p className="text-[8px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Reposts</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-navy-900 dark:text-cream-50">{data.replies ?? 0}</p>
            <p className="text-[8px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Replies</p>
          </div>
        </div>

        {/* View on Bluesky */}
        {data.webUrl && (
          <a
            href={data.webUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center text-[10px] font-black uppercase tracking-widest bg-blue-500/10 hover:bg-blue-500 text-blue-400 hover:text-white py-2.5 rounded-lg transition mb-4"
          >
            View on Bluesky
          </a>
        )}

        {/* Thread Replies */}
        {hasReplies && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Comments</h4>
              <div className="flex-1 h-px bg-cream-200 dark:bg-navy-600/40" />
            </div>
            <div className="divide-y divide-cream-200 dark:divide-navy-600/30">
              {data.topReplies!.map((reply, i) => (
                <ReplyThread key={i} reply={reply} />
              ))}
            </div>
          </div>
        )}

        {!hasReplies && (
          <p className="text-[10px] text-navy-400 dark:text-navy-300 text-center italic">No comments yet. Be the first to reply on Bluesky.</p>
        )}
      </div>
    </div>
  );
}

export default OfficialDetailPage;
